import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { closeContextMenu, goBack } from '../ActiveGame/actions';
import {
  selectActiveContextMenu,
  selectActiveContextUserId,
  selectContextMenuDepth,
} from '../ActiveGame/selectors';
import { selectBoardChoice } from '../Board/selectors';
import { selectConfirmed } from '../ConfirmButton/selectors';
import { selectCard } from '../Card/selectors';
import { Button } from '../Controls';
import { Backdrop, Body, Header, Sheet, Title } from './styled';

/**
 * A menu for one thing on the board, shown as a sheet over the game: from the bottom when the phone is upright, from
 * the side when it is turned. Only the menu named in the store is open, and none while the player is picking a spot
 * on the board, since the sheet would cover it. Escape closes it, unless a confirm prompt or a full-screen card is
 * open over it, which Escape closes first.
 */
const ContextMenu = ({ userId, menu, title, children }) => {
  const dispatch = useDispatch();
  const activeContextMenu = useSelector(selectActiveContextMenu);
  const activeContextUserId = useSelector(selectActiveContextUserId);
  const boardChoice = useSelector(selectBoardChoice);
  const depth = useSelector(selectContextMenuDepth);
  const confirming = useSelector(selectConfirmed) !== null;
  const zoomedCard = useSelector(selectCard);

  const open = menu === activeContextMenu && userId === activeContextUserId && !boardChoice;

  useEffect(() => {
    if (!open || confirming || zoomedCard) {
      return undefined;
    }

    const closeOnEscape = (event) => event.key === 'Escape' && dispatch(closeContextMenu());
    document.addEventListener('keydown', closeOnEscape);

    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [open, confirming, zoomedCard, dispatch]);

  if (!open) {
    return null;
  }

  const close = () => dispatch(closeContextMenu());

  return createPortal(
    <>
      <Backdrop onClick={close} />
      <Sheet role="dialog" aria-label={title}>
        <Header>
          {depth > 1 && <Button onClick={() => dispatch(goBack())}>Back</Button>}
          <Title>{title}</Title>
          <Button onClick={close} aria-label="Close">Close</Button>
        </Header>
        <Body>{children}</Body>
      </Sheet>
    </>,
    document.body,
  );
};

ContextMenu.propTypes = {
  userId: PropTypes.number.isRequired,
  menu: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default ContextMenu;
